import Style from './useCreateFormConfigModal.module.scss';
import { useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useModal } from 'src/application/hooks/useModal';
import { DialogModal } from 'src/components/dialogModal/dialogModal.component';
import { HookFormTextInput } from 'src/hookFormControls/hookFormTextInput';
import { hookFormRules } from '../../fillingModal/react-hook-form-helper';
import { ConfirmResult } from 'src/components/dialogModal/useConfirmationModal';
import { ApiException, ValidationProblemDetails } from 'src/services/api/api-client';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { logger } from 'src/application/logging/logging';

export type FormData = {
  title: string;
};

export const useCreateFormConfigModal = () => {
  const modal = useModal();
  const callbackRef = useRef<(data: FormData) => Promise<void>>();
  const promiseRef = useRef<(result: ConfirmResult) => void>();

  const { t } = useScopedTranslation('GeneralFormBuilder.CreateFormConfigModal');
  const form = useForm<FormData>();

  const submitHandler = form.handleSubmit(async (data) => {
    try {
      await callbackRef.current?.(data);
      promiseRef?.current?.(ConfirmResult.Confirm);
      modal.closeModal();
    } catch (error) {
      const isValidationProblemDetails = error instanceof ValidationProblemDetails;
      if (isValidationProblemDetails) {
        Object.entries(error.errors).forEach(([key, value]) => {
          if (form.control._names.mount.has(key.toLowerCase())) {
            // @ts-ignore
            form.setError(key.toLowerCase(), {
              message: value.join(', '),
            });
          }
        });
        if (error.detail) form.setError('root', { message: error.detail });
      }

      const isApiException = ApiException.isApiException(error);
      if (isApiException) {
        logger().error(error.message, error);
        form.setError('root', { message: error.message });
      }
    }
  });

  const openHandler = useCallback(
    async (options: { callback: (data: FormData) => Promise<void>; initialValues?: FormData }) => {
      form.reset(options.initialValues);
      callbackRef.current = options.callback;
      modal.openModal();
      return new Promise<ConfirmResult>((resolve) => (promiseRef.current = resolve));
    },
    [form, modal],
  );

  const closeHandler = useCallback(() => {
    modal.closeModal();
    promiseRef?.current?.(ConfirmResult.Cancel);
  }, [modal]);

  return {
    open: openHandler,
    modal: (
      <DialogModal
        bodyClassName={Style.modalBody}
        visible={modal.visible}
        onHide={closeHandler}
        title={t('ModalTitle')}
        isDisabled={form.formState.isSubmitting}
        footer={{
          errors: form.formState.errors.root?.message,
          leftButton: {
            text: t('CancelButtonText'),
            onClick: closeHandler,
            disabled: form.formState.isSubmitting,
          },
          rightButton: {
            text: t('SaveButtonText'),
            onClick: submitHandler,
            isLoading: form.formState.isSubmitting,
            disabled: form.formState.isSubmitting,
          },
        }}
      >
        <HookFormTextInput
          control={form.control}
          name={'title'}
          labelProps={{
            text: t('Fields.Title.Label'),
          }}
          placeholder={t('Fields.Title.Placeholder')}
          rules={{ ...hookFormRules({ required: true }) }}
          disabled={form.formState.isSubmitting}
        />
      </DialogModal>
    ),
  };
};
